import React from 'react';
import {
  Bookmark01Icon,
  Calendar03Icon,
  DnaIcon,
  Edit02Icon,
  // Folder01Icon,
  LicenseIcon,
  Link04Icon,
  LinkSquare01Icon,
  Loading03Icon,
  Medicine02Icon,
  News01Icon,
  Pdf01Icon,
  Upload02Icon,
  ArticleIcon,
  UserMultiple02Icon,
  MedicineBottle01Icon,
  Dna01Icon,
  AnalysisTextLinkIcon,
} from '../../../components/icons';
import { Input, message, Tabs, Modal, Form, Select, InputNumber, Checkbox } from 'antd';
import PDFDocView from '../../../../pages/pubmedData/DocView';
import BookMarkModal from '../../../components/bookMarkModal';
import UploadPDF from '../../../../pages/pubmedData/importPDF';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { BookmarkService, DocService } from '../../../../services/dataService';
import moment from 'moment';
import { useAuth } from '../../../../authContext';
import { Copy, Share2 } from 'lucide-react';

const SUMMARY_KEY = ['objective', 'population_characteristics', 'methods', 'endpoints_measured', 'conclusion'];

// const FILTER_KEYS = ['tissue_type'];
const SUMMARY_KEY_LABEL = {
  objective: 'Objective',
  population_characteristics: 'Population Characteristics',
  methods: 'Methods',
  endpoints_measured: 'Endpoints Measured',
  conclusion: 'Conclusions',
};

const KEY_LABEL = {
  endpoints: 'Endpoints',
  relevant_endpoints: 'Relevant Endpoints',
  study_type: 'Study Type',
  primary_tissue_type: 'Primary Tissue Type',
  drugs: 'Drugs Studied',
  cancer_types: 'Cancer Types Studied',
  is_drug_evaluated: 'Is Queried Drug Evaluated',
  num_patients: 'Number of Patients',
  ta: 'Therapeutic Association',
  relevance: 'Relevance',
  is_adhoc: 'Is a Ad-Hoc/Post-Hoc Study',
  is_combination_therapy: 'Combination',
  nccn_cancer_types: 'NCCN Cancer Type',
  old_primary_tissue_type: 'Primary Tissue Type',
  publication_country: 'Publication Country',
};

const ARTICLE_TYPE = ['study_type', 'is_adhoc', 'num_patients', 'publication_country'];
const DRUGS = ['is_drug_evaluated', 'is_combination_therapy'];

const STUDY_TYPE_OPTIONS = [
  'Clinical Trial: RCT',
  'Clinical Trial: non-RCT',
  'Review: meta-analysis',
  'Observational study',
  'Review: other',
  'Case study',
  'Preclinical',
  'Other',
];

const IMPACT_TYPE_OPTIONS = ['positive', 'negative', 'inconclusive', 'not_mentioned'];

// Keep in sync with backend NCCN_CANCER_TYPES
const NCCN_CANCER_TYPES = [
  'Acute Lymphoblastic Leukemia',
  'Acute Myeloid Leukemia',
  'Ampullary Adenocarcinoma',
  'Anal Carcinoma',
  'Basal Cell Skin Cancer',
  'B-Cell Lymphomas',
  'Biliary Tract Cancers',
  'Bladder Cancer',
  'Bone Cancer',
  'Breast Cancer',
  'Central Nervous System Cancers',
  'Cervical Cancer',
  'Chronic Lymphocytic Leukemia/Small Lymphocytic Lymphoma',
  'Chronic Myeloid Leukemia',
  'Colorectal Cancer',
  'Dermatofibrosarcoma Protuberans',
  'Esophageal and Esophagogastric Junction Cancers',
  'Gastric Cancer',
  'Gastrointestinal Stromal Tumors',
  'Gestational Trophoblastic Neoplasia',
  'Hairy Cell Leukemia',
  'Head and Neck Cancers',
  'Hepatocellular Carcinoma',
  'Histiocytic Neoplasms',
  'Hodgkin Lymphoma',
  'Kaposi Sarcoma',
  'Kidney Cancer',
  'Melanoma: Cutaneous',
  'Melanoma: Uveal',
  'Merkel Cell Carcinoma',
  'Mesothelioma: Peritoneal',
  'Mesothelioma: Pleural',
  'Multiple Myeloma',
  'Myelodysplastic Syndromes',
  'Myeloid/Lymphoid Neoplasms with Eosinophilia and Tyrosine Kinase Gene Fusions',
  'Myeloproliferative Neoplasms',
  'Neuroblastoma',
  'Neuroendocrine and Adrenal Tumors',
  'Non-Small Cell Lung Cancer',
  'Ovarian Cancer/Fallopian Tube Cancer/Primary Peritoneal Cancer',
  'Pancreatic Adenocarcinoma',
  'Penile Cancer',
  'Primary Cutaneous Lymphomas',
  'Prostate Cancer',
  'Small Bowel Adenocarcinoma',
  'Small Cell Lung Cancer',
  'Soft Tissue Sarcoma',
  'Squamous Cell Skin Cancer',
  'Systemic Mastocytosis',
  'T-Cell Lymphomas',
  'Testicular Cancer',
  'Thymomas and Thymic Carcinomas',
  'Thyroid Carcinoma',
  'Uterine Neoplasms',
  'Vaginal Cancer',
  'Vulvar Cancer',
  'Waldenström Macroglobulinemia/Lymphoplasmacytic Lymphoma',
  'Wilms Tumor (Nephroblastoma)',
  'Hematological Malignancies',
  'Broad Category',
];

const RenderKey = ({ label, value }) => {
  if (typeof value === 'boolean') {
    value = value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      value = <span className="text-sm text-neutral-400">No value extracted</span>;
    } else {
      value = value.map((v) => (
        <span key={v} className="bg-neutral-100 text-neutral-600 px-2 rounded-md text-sm">
          {v}
        </span>
      ));
    }
  }

  if (value === null || value === undefined || value === '') {
    value = <span className="text-sm text-neutral-400">No values extracted</span>;
  }

  return (
    <div className="flex  gap-1 border-b border-neutral-100 py-2 w-[700px]">
      <div className="text-sm font-semibold w-1/2">{label}</div>
      <div className="text-sm w-1/2 flex flex-wrap gap-1">{value}</div>
    </div>
  );
};

const CancerSpecificDetails = ({ article }) => {
  return (
    <div className="flex flex-col ">
      <div className="text-base text-neutral-500 h-full overflow-y-auto w-[700px] flex border-b border-neutral-100 pb-4">
        <div className="text-sm font-semibold basis-1/2">NCCN Cancer Types</div>
        <div className="text-sm basis-1/2 flex flex-wrap gap-1">
          {article.task_specific_details.nccn_cancer_types?.join(', ')}
        </div>
      </div>
      <div className="text-base text-neutral-500 h-full overflow-y-auto mt-4 w-[700px] flex">
        <div className="text-sm font-semibold basis-1/2">Primary Tissue Type</div>
        <div className="text-sm basis-1/2 flex flex-wrap gap-1">
          {article.task_specific_details.primary_tissue_type}
        </div>
      </div>
    </div>
  );
};

const ExtractedDetailsEditModal = ({ visible, article, onCancel, onSubmit, confirmLoading }) => {
  const [form] = Form.useForm();

  const initialValues = {
    study_type: article?.study_type,
    is_adhoc: article?.is_adhoc,
    num_patients: article?.num_patients,
    publication_country: article?.publication_country,
    primary_tissue_type: article?.task_specific_details?.primary_tissue_type,
    nccn_cancer_types: article?.task_specific_details?.nccn_cancer_types || [],
    is_drug_evaluated: article?.is_drug_evaluated,
    is_combination_therapy: article?.is_combination_therapy,
    impact_type: article?.impact_type,
  };

  React.useEffect(() => {
    if (visible && article) {
      form.setFieldsValue(initialValues);
    }
  }, [visible, article, form]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        // Only send fields that actually changed
        const changedValues = {};
        Object.keys(values).forEach((key) => {
          const current = values[key];
          const initial = initialValues[key];

          let isEqual = false;
          if (Array.isArray(current) || Array.isArray(initial)) {
            const currArr = current || [];
            const initArr = initial || [];
            isEqual = JSON.stringify(currArr) === JSON.stringify(initArr);
          } else {
            isEqual = current === initial;
          }

          if (!isEqual && current !== undefined) {
            changedValues[key] = current;
          }
        });

        if (Object.keys(changedValues).length === 0) {
          message.info('No changes to submit');
          onCancel?.();
          return;
        }

        onSubmit?.(changedValues);
      })
      .catch(() => {});
  };

  return (
    <Modal
      visible={visible}
      title="Suggest Changes to Extracted Details"
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      destroyOnClose
      // className="max-h-[80vh]"
    >
      <Form form={form} layout="vertical" initialValues={initialValues} className="max-h-[60vh] pr-4 overflow-y-auto">
        <Form.Item name="study_type" label="Study Type">
          <Select allowClear showSearch options={STUDY_TYPE_OPTIONS.map((v) => ({ label: v, value: v }))} />
        </Form.Item>

        <Form.Item name="is_adhoc" valuePropName="checked">
          <Checkbox>Ad-hoc / Post-hoc Study</Checkbox>
        </Form.Item>

        <Form.Item name="num_patients" label="Number of Patients">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="publication_country" label="Publication Country">
          <Input />
        </Form.Item>

        <Form.Item name="primary_tissue_type" label="Primary Tissue Type">
          <Input />
        </Form.Item>

        <Form.Item name="nccn_cancer_types" label="NCCN Cancer Types">
          <Select
            mode="multiple"
            allowClear
            showSearch
            options={NCCN_CANCER_TYPES.map((v) => ({ label: v, value: v }))}
          />
        </Form.Item>

        <Form.Item name="is_drug_evaluated" valuePropName="checked">
          <Checkbox>Is queried drug evaluated</Checkbox>
        </Form.Item>

        <Form.Item name="is_combination_therapy" valuePropName="checked">
          <Checkbox>Combination therapy</Checkbox>
        </Form.Item>

        <Form.Item name="impact_type" label="Therapeutic Association">
          <Select
            allowClear
            options={IMPACT_TYPE_OPTIONS.map((v) => ({
              label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
              value: v,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const ExtractedDetails = ({ article }) => {
  return (
    <div className="text-base text-neutral-600 h-full overflow-y-auto">
      <div className="mb-12 text-neutral-500">
        <div className="text-sm font-semibold text-blue-500 flex items-center gap-2 mb-2">
          <ArticleIcon className="w-4 h-4 inline text-blue-500" /> Article Details
        </div>
        {ARTICLE_TYPE.map((key) => (
          <RenderKey key={key} label={KEY_LABEL[key]} value={article[key]} />
        ))}
      </div>

      <div className="mb-12">
        <div className="text-sm font-semibold text-blue-500 flex items-center gap-2 mb-4">
          <ArticleIcon className="w-4 h-4 inline text-blue-500" /> Cancer Specific Details
        </div>

        <CancerSpecificDetails article={article} />
      </div>

      <div className="mb-12 text-neutral-500">
        <div className="text-sm font-semibold text-blue-500 mb-2 flex items-center gap-2">
          <MedicineBottle01Icon className="w-4 h-4 inline text-blue-500" />
          Drug Details
        </div>
        <div className="flex w-[700px]">
          <div className="basis-1/2">
            <div className="text-sm font-semibold text-neutral-500 mb-2">Drugs Studied</div>
          </div>
          <div className="flex flex-col gap-1 basis-1/2 ">
            {article.drugs &&
              article.drugs.map((drug, idx) => (
                <div key={idx} className="text-sm text-neutral-500">
                  {drug.name}
                  <span
                    className={`text-xs ml-2 px-2 py-0.5 rounded-md ${
                      drug.is_studied ? 'bg-green-100 text-green-500' : 'bg-yellow-100 text-yellow-500'
                    }`}
                  >
                    {drug.is_studied ? 'Studied' : 'Not Studied'}
                  </span>
                  <span
                    className={`text-xs ml-2 px-2 py-0.5 rounded-md ${
                      drug.is_randomized ? 'bg-green-100 text-green-500' : 'bg-yellow-100 text-yellow-500'
                    }`}
                  >
                    {drug.is_randomized ? 'Randomized' : 'Not Randomized'}
                  </span>
                </div>
              ))}
            {article.drugs && article.drugs.length === 0 && (
              <div className="text-sm text-neutral-500">No drugs studied</div>
            )}
          </div>
        </div>
        <div className=" text-neutral-500">
          {DRUGS.map((key) => (
            <RenderKey key={key} label={KEY_LABEL[key]} value={article[key]} />
          ))}
        </div>
      </div>

      <div className="mb-12">
        <div className="text-sm font-semibold text-blue-500 flex items-center gap-2 mb-2">
          <Dna01Icon className="w-4 h-4 inline text-blue-500" />
          Disease Details
        </div>
        <div className="flex w-[700px]">
          <div className="basis-1/2">
            <div className="text-sm font-semibold text-neutral-500 mb-2">Disease Types</div>
          </div>
          <div className="flex flex-col gap-1 basis-1/2">
            {article.diseases &&
              article.diseases.map((disease, idx) => (
                <div key={idx} className="text-sm text-neutral-500">
                  {disease.name}
                  <span
                    className={`text-xs ml-2 px-2 py-0.5 rounded-md ${
                      disease.is_studied ? 'bg-green-100 text-green-500' : 'bg-yellow-100 text-yellow-500'
                    }`}
                  >
                    {disease.is_studied ? 'Studied' : 'Not Studied'}
                  </span>
                </div>
              ))}
            {article.diseases && article.diseases.length === 0 && (
              <div className="text-sm text-neutral-500">No disease types</div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-12">
        <div className="text-sm font-semibold text-blue-500 flex items-center gap-2 mb-2">
          <AnalysisTextLinkIcon className="w-4 h-4 inline text-blue-500" />
          Outcomes
        </div>
        <div className="mb-4 text-neutral-500">
          <RenderKey label="Therapeutic Association" value={article.impact_type} />
        </div>

        <div className="flex w-[700px]">
          <div className="basis-1/2">
            <div className="text-sm font-semibold text-neutral-500 mb-2">Endpoints</div>
          </div>
          <div className="flex flex-col gap-1 basis-1/2">
            {article.endpoints &&
              article.endpoints.map((endpoint, idx) => (
                <div key={idx} className="text-sm text-neutral-500">
                  {endpoint.name}
                  <span className={`text-xs ml-2 px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-500`}>
                    {endpoint.category}
                  </span>
                </div>
              ))}
            {article.endpoints && article.endpoints.length === 0 && (
              <div className="text-sm text-neutral-500">No endpoints</div>
            )}
          </div>
        </div>
      </div>

      {/* <div className="mb-12">
        <div className="text-sm font-semibold text-neutral-500 flex items-center gap-2 mb-2">
          <AnalysisTextLinkIcon className="w-4 h-4 inline text-neutral-500" />
          Outcomes
        </div>
        {OUTCOMES.map((key) => (
          <RenderKey key={key} label={KEY_LABEL[key]} value={article[key]} />
        ))}
        <div className="text-sm font-semibold mt-4">Endpoints Stats</div>
        {article.endpoint_stats.map((details, idx) => (
          <EndpointStats key={idx} details={details} />
        ))}
      </div> */}
    </div>
  );
};
const Button = ({ children, disabled, onClick, loading }) => {
  return (
    <button
      className={`text-sm px-2 py-1 rounded-md bg-neutral-100 text-neutral-600 transition hover:bg-neutral-200 active:bg-neutral-300 flex items-center gap-2 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${loading ? 'cursor-not-allowed opacity-50' : ''}`}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading && <Loading03Icon className="animate-spin" />}
      {children}
    </button>
  );
};

const CommentSection = ({ articleId }) => {
  const auth = useAuth();
  const [commentText, setCommentText] = React.useState('');
  const comments = useQuery(['comments', articleId], () => DocService.getComments(articleId), {});

  const saveComment = useMutation(
    (data) =>
      DocService.addComment(articleId, {
        ...data,
        pubmed_id: articleId,
        comment_by: auth.user.username,
      }),
    {
      onSuccess: () => {
        comments.refetch();
        setCommentText('');
      },
    },
  );
  return (
    <div className="relative h-full overflow-hidden">
      <div className="overflow-y-auto h-full">
        <div className="flex flex-col gap-2  pb-32">
          {comments.data?.results.map((comment, idx) => (
            <div
              key={comment.id}
              className={`flex flex-col gap-1 p-2 border-b ${
                idx === comments.data?.results.length - 1 ? 'border-b-0' : 'border-b'
              }`}
            >
              <div className="text-sm text-neutral-600">{comment.comment}</div>
              <div className="flex gap-3 text-neutral-400">
                <div className="text-xs">{moment(comment.created_at).format('MMM DD, YYYY')}</div>
                <div className="text-xs capitalize">{comment.comment_by}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-white">
        <Input.TextArea value={commentText} onChange={(e) => setCommentText(e.target.value)} className="mb-2 mt-4" />
        <Button onClick={() => saveComment.mutate({ comment: commentText })} loading={saveComment.isLoading}>
          <span className="px-4 py-1 "> Save</span>
        </Button>
      </div>
    </div>
  );
};

const ArticleView = ({ articleId, fullView = false }) => {
  const [bookMarkFlag, setBookMarkFlag] = React.useState(false);
  const [pdfImportFlag, setPdfImportFlag] = React.useState(false);
  // const [folderFlag, setFolderFlag] = React.useState(false);
  const [pdfViewFlag, setPdfViewFlag] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('summary');
  const [editDetailsFlag, setEditDetailsFlag] = React.useState(false);
  const queryClient = useQueryClient();
  const auth = useAuth();

  const articleDetailQuery = useQuery(['articleDetail', articleId], () => {
    return DocService.get(articleId);
  });

  const articleDetail = articleDetailQuery.data;

  const bookmarkMutation = useMutation((bookmarkId) => {
    return BookmarkService.addItem({
      bookmark_id: bookmarkId,
      item_id: articleId,
    });
  });

  const suggestChangesMutation = useMutation(
    (values) => {
      const suggestionId = `${articleDetail.id}-${Date.now()}`;
      const payload = {
        id: suggestionId,
        suggested_by: auth.user.username,
        suggested_at: new Date().toISOString(),
        resolved_at: null,
        status: 'pending',
        ...values,
      };
      return DocService.updateSuggestions(articleDetail.id, payload);
    },
    {
      onSuccess: () => {
        message.success('Suggested changes submitted');
        setEditDetailsFlag(false);
        queryClient.invalidateQueries(['comments', articleId]);
      },
      onError: () => {
        message.error('Failed to submit suggested changes');
      },
    },
  );

  // const openPDFViewer = () => {
  //   setPdfViewFlag(true);
  // };

  const onAddToBookmark = async (bookmarkId) => {
    message.loading('Adding to bookmark');
    await bookmarkMutation.mutateAsync(bookmarkId);
    message.destroy();
    message.success('Added to bookmark');
    queryClient.invalidateQueries(['bookmarkItems']);
    setBookMarkFlag(false);
  };

  if (!articleDetail) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {pdfViewFlag && (
        <PDFDocView
          docId={articleDetail.pubmed_id}
          pmid={articleDetail.pubmed_id}
          visible={pdfViewFlag}
          onClose={() => {
            setPdfViewFlag(false);
          }}
        />
      )}
      {pdfImportFlag && (
        <UploadPDF
          itemId={articleId}
          visible={pdfImportFlag}
          onCancel={() => setPdfImportFlag(false)}
          onConfirm={() => {
            setPdfImportFlag(false);
            queryClient.invalidateQueries(['pubmedItem', articleId]);
          }}
        />
      )}
      {bookMarkFlag && (
        <BookMarkModal visible={bookMarkFlag} setVisible={setBookMarkFlag} setSelectedBookmark={onAddToBookmark} />
      )}
      <div className="px-4 flex flex-col gap-2 h-full overflow-y-auto">
        <div className={`flex gap-2 flex-wrap mt-6 ${fullView ? 'mt-0' : 'mt-6'}`}>
          {articleDetail.is_relevant ? (
            <span className="text-sm text-green-500 bg-green-50 border border-green-300 px-2 py-0.5 rounded-lg">
              Include
            </span>
          ) : (
            <span className="text-sm text-red-500 bg-red-50 border border-red-300 px-2 py-0.5 rounded-lg">Exclude</span>
          )}
          {articleDetail.nccn_tissue_type && (
            <span className="text-sm text-neutral-700 bg-neutral-50 border border-neutral-300 px-2 py-0.5 rounded-lg flex items-center gap-1">
              <DnaIcon className="w-4 h-4" />
              {articleDetail.nccn_tissue_type}
            </span>
          )}
          {articleDetail.drug && (
            <span className="text-sm text-neutral-700 bg-neutral-50 border border-neutral-300 px-2 py-0.5 rounded-lg flex items-center gap-1">
              <Medicine02Icon className="w-4 h-4" />
              {articleDetail.drug}
            </span>
          )}

          {articleDetail.pubmed_id && (
            <button
              className="text-sm text-neutral-700 bg-neutral-50 border border-neutral-300 px-2 py-0.5 rounded-lg flex items-center gap-1"
              onClick={() => {
                navigator.clipboard.writeText(`${articleDetail.pubmed_id}`);
              }}
            >
              <Copy className="w-4 h-4" />
              PubMed
            </button>
          )}
          {articleDetail.doi && (
            <button
              className="text-sm text-neutral-700 bg-neutral-50 border border-neutral-300 px-2 py-0.5 rounded-lg flex items-center gap-1"
              onClick={() => {
                navigator.clipboard.writeText(articleDetail.doi);
              }}
            >
              <Copy className="w-4 h-4" />
              DOI
            </button>
          )}
        </div>
        <h2 className="text-xl font-semibold ">{articleDetail.title}</h2>

        <div className="flex gap-2 flex-wrap">
          <span className="text-sm text-neutral-500  leading-[160%]">
            <Calendar03Icon className="w-4 h-4 text-neutral-500 inline mr-2" />
            {articleDetail.published_date}
            {articleDetail.authors?.length > 0 && (
              <>
                <UserMultiple02Icon className="w-4 h-4 text-neutral-500  inline mx-2 ml-4" />
                {articleDetail.authors?.join(', ')}
              </>
            )}
          </span>
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          {articleDetail.pubmed_id && (
            <Button
              onClick={() => {
                window.open(`https://pubmed.ncbi.nlm.nih.gov/${articleDetail.pubmed_id}`, '_blank');
              }}
            >
              <Link04Icon className="w-4 h-4" />
              PubMed
            </Button>
          )}
          {articleDetail.doi && (
            <Button
              onClick={() => {
                if (articleDetail.doi.startsWith('http')) {
                  window.open(articleDetail.doi, '_blank');
                } else {
                  window.open(`https://doi.org/${articleDetail.doi}`, '_blank');
                }
              }}
            >
              <News01Icon className="w-4 h-4" />
              DOI
            </Button>
          )}

          <Button
            onClick={() => {
              window.open(articleDetail.full_text_url, '_blank');
            }}
            disabled={!articleDetail.full_text_url}
          >
            <LicenseIcon className="w-4 h-4" />
            Full Text
          </Button>
          <Button onClick={() => setPdfViewFlag(true)} disabled={!articleDetail.has_pdf}>
            <Pdf01Icon className="w-4 h-4" />
            View PDF
          </Button>
          <Button onClick={() => setPdfImportFlag(true)}>
            <Upload02Icon className="w-4 h-4" />
            Upload PDF
          </Button>
          {fullView && (
            <Button
              onClick={() => {
                window.open(`/article/${articleId}`, '_blank');
              }}
            >
              <LinkSquare01Icon className="w-4 h-4" />
              Open in New Tab
            </Button>
          )}

          <Button
            onClick={() => {
              const articleUrl = `${window.location.origin}/article/${articleId}`;
              navigator.clipboard
                .writeText(articleUrl)
                .then(() => {
                  message.success('Article link copied to clipboard!');
                })
                .catch(() => {
                  message.error('Failed to copy link');
                });
            }}
          >
            <Share2 className="w-4 h-4" />
            Share
          </Button>

          <Button onClick={() => setBookMarkFlag(true)}>
            <Bookmark01Icon className="w-4 h-4" />
            Bookmark
          </Button>

          {/* <Button onClick={() => setFolderFlag(true)}>
            <Folder01Icon className="w-4 h-4" />
            Folder
          </Button> */}
          <Button
            onClick={() => {
              setEditDetailsFlag(true);
            }}
          >
            <Edit02Icon className="w-4 h-4" />
            Suggest Changes
          </Button>
        </div>
        <div className="flex-1 ">
          <Tabs
            defaultActiveKey="summary"
            className="sticky top-0 bg-white z-10"
            onChange={(key) => {
              // console.log(key);
              setActiveTab(key);
            }}
            activeKey={activeTab}
          >
            <Tabs.TabPane tab="Summary" key="summary" className="text-neutral-600  h-full"></Tabs.TabPane>
            <Tabs.TabPane tab="Abstract" key="abstract" className="h-full"></Tabs.TabPane>
            <Tabs.TabPane
              tab="Extracted Details"
              key="extracted_details"
              className="h-full overflow-y-auto"
            ></Tabs.TabPane>
            <Tabs.TabPane tab="Comments" key="comments" className="h-full overflow-y-auto">
              <CommentSection articleId={articleId} />
            </Tabs.TabPane>
            {/* <Tabs.TabPane tab="LLM Questions" key="llm_questions" className="h-full overflow-y-auto"></Tabs.TabPane> */}
          </Tabs>
          {activeTab == 'summary' && (
            <div className="text-sm text-neutral-600 h-full">
              {SUMMARY_KEY.map((key) => (
                <div key={key} className="my-2">
                  <div className="text-sm font-semibold ">{SUMMARY_KEY_LABEL[key]}</div>
                  <div>{articleDetail?.summary?.[key]}</div>
                </div>
              ))}
            </div>
          )}
          {activeTab == 'abstract' && (
            <div className="text-sm text-neutral-600 overflow-y-auto h-full">{articleDetail?.abstract}</div>
          )}
          {activeTab == 'extracted_details' && articleDetail && <ExtractedDetails article={articleDetail} />}
          {activeTab == 'comments' && <CommentSection articleId={articleId} />}
        </div>
      </div>
      {editDetailsFlag && articleDetail && (
        <ExtractedDetailsEditModal
          visible={editDetailsFlag}
          article={articleDetail}
          onCancel={() => setEditDetailsFlag(false)}
          onSubmit={(values) => suggestChangesMutation.mutate(values)}
          confirmLoading={suggestChangesMutation.isLoading}
        />
      )}
    </>
  );
};

export default ArticleView;
